<template>
  <section class="PromotionalTile PromotionalTile--Cart">
    <h2 class="e2 PromotionalTile__Title" v-if="title">
      <Icon icon="sparkle" />
      <span>{{ title }}</span>
    </h2>
    <ul class="PromotionalTile__Offers">
      <li v-for="(tier, index) in tiers" :key="tier.code" class="PromotionalTile__Offer">
        <div class="PromotionalTile__Icon" :class="activeIconClass(tier)" :data-tier="`${(index += 1)}`">
          <Icon icon="price-tag" />
        </div>
        <p class="t9 PromotionalTile__Offer-Messaging">
          <span>{{ tierThresholdMessaging(tier) }}</span>
          <span>{{ tierSavingsMessaging(tier) }}</span>
        </p>
      </li>
    </ul>
    <progress id="TieredDiscount-Progress" class="Progress" aria-label="discount-progress-bar" :style="{ '--tiered-percentage': `${percentageComplete}%` }" :value="percentageComplete" :max="100"></progress>
    <label for="TieredDiscount-Progress" class="Progress__Label" v-if="currentSavings || tierPromotion">
      <p v-if="currentSavings" class="t5">{{ currentSavings }}</p>
      <p v-if="tierPromotion" class="p5">{{ tierPromotion }}</p>
    </label>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { formattedPrice } from '@utils/price-helpers';
import Icon from '../Global/Icon.vue';

import { useCartStore } from './Composables/Store';
const store = useCartStore();

const title = computed(() => {
  const data = store?.$state?.tieredDiscount ?? false;
  if (!data) return false;

  const cartTitle = data?.cart_title && data.cart_title !== '' ? data.cart_title : false;
  const title = data?.title && data?.title !== '' ? data.title : false;

  if (cartTitle) return cartTitle;
  else if (title) return title;
  else return false;
});

const tiers = computed(() => {
  const data = store?.$state?.tieredDiscount ?? false;

  const tiers = data ? [data.tier1, data.tier2, data.tier3] : [];
  const validTiers = tiers.filter((tier) => tier?.code && tier?.code !== '');
  return validTiers;
});

const tierThresholdMessaging = computed(() => {
  return (tier) => {
    const threshold = formattedPrice(tier.threshold);
    const message = `Spend ${threshold}`;
    return message;
  };
});

const tierSavingsMessaging = computed(() => {
  return (tier) => {
    const savings = `${tier.amount}%`;
    const message = `Save ${savings}`;
    return message;
  };
});

const activeIconClass = computed(() => {
  return (tier) => {
    const threshold = tier.threshold;
    const subtotal = store.getTieredSubtotal;

    if (subtotal >= threshold) {
      return 'PromotionalTile__Icon--Active';
    }

    return '';
  };
});

const percentageComplete = computed(() => {
  const threshold = parseInt(maximumThreshold.value);
  const subtotal = store.getTieredSubtotal;

  if (subtotal <= 0) return 0;

  let percentage = (subtotal / threshold) * 100;
  percentage = Math.round(percentage * 10) / 10;

  return Math.min(100, percentage);
});

const currentTier = computed(() => {
  const data = [...tiers.value].reverse();
  const current = data.find((tier) => store.getTieredSubtotal >= tier.threshold);
  return current;
});

const tierPromotion = computed(() => {
  const tier = currentTier.value;
  const firstTierNotMet = !tier;
  const subtotal = store.getTieredSubtotal;
  const allTiersExceeded = subtotal >= maximumThreshold.value;

  if (firstTierNotMet) {
    const firstTier = tiers.value[0];
    const remainder = formattedPrice(parseFloat(firstTier.threshold - subtotal));
    return `Spend ${remainder} more to save ${firstTier.amount}%`;
  }

  if (allTiersExceeded) {
    return false;
  }

  const currentTierIndex = tiers.value.findIndex((tier) => tier.amount === currentTier.value.amount);
  const nextTier = tiers.value[currentTierIndex + 1];
  const remainder = formattedPrice(parseFloat(nextTier.threshold - subtotal));

  return `Spend ${remainder} more to save ${nextTier.amount}%`;
});

const currentSavings = computed(() => {
  const tier = currentTier.value;
  const firstTierNotMet = !tier;

  if (firstTierNotMet) {
    return false;
  }

  return `You saved ${tier.amount}% on applicable products!`;
});
const maximumThreshold = computed(() => tiers.value[tiers.value.length - 1].threshold);
</script>
